/**
 * Calculatrice de Potentiel Énergétique AEP - ELECTRON INDUSTRIE
 * 
 * Application autonome pour calculer le potentiel énergétique d'un réseau 
 * d'Alimentation en Eau Potable (AEP) avec contrainte de pression aval minimale.
 * 
 * CONTRAINTE MAJEURE: La pression aval ne doit jamais descendre en dessous de 3 bars.
 * Seule la pression exploitable (amont - 3 bars) est valorisable.
 */

import React from 'react';
import { Zap, AlertTriangle, Info, Droplets } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { TooltipProvider } from './ui/tooltip';
import { ParametersCard } from './aep/ParametersCard';
import { ResultsCard } from './aep/ResultsCard';
import { CombinationsTable } from './aep/CombinationsTable';
import { useAEPCalculator } from '../hooks/useAEPCalculator';
import { LOGO_URL, PRESSION_AVAL_MIN } from '../lib/aep-constants';

/**
 * Composant d'en-tête avec logo
 */
const Header = () => (
  <header className="text-center space-y-4 mb-8">
    {/* Logo Electron Industrie */}
    <div className="flex justify-center mb-4">
      <img 
        src={LOGO_URL} 
        alt="Electron Industrie - Bureau d'études Industrie Mécanique" 
        className="h-24 md:h-32 w-auto object-contain"
        data-testid="electron-logo"
      />
    </div>
    
    <div className="flex items-center justify-center gap-3">
      <Zap className="h-8 w-8 text-[#1E3A8A]" />
      <h1 
        className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#0F172A] tracking-tight"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        data-testid="calculator-title"
      >
        Calculatrice de potentiel énergétique AEP
      </h1>
    </div>
    <p className="text-sm md:text-base text-[#475569] max-w-3xl mx-auto">
      Estimation du potentiel récupérable au-dessus du seuil de pression aval minimal de <strong>{PRESSION_AVAL_MIN} bars</strong>
    </p>
    
    {/* Alerte contrainte pression */}
    <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-sm text-sm">
      <AlertTriangle className="h-4 w-4" />
      <span>Contrainte: La pression aval ne doit jamais descendre en dessous de {PRESSION_AVAL_MIN} bars</span>
    </div>
  </header>
);

/**
 * Composant de notes techniques
 */
const TechnicalNotes = () => (
  <Card className="border-l-4 border-l-[#1E3A8A] border-[#CBD5E1] bg-[#F1F5F9] rounded-sm shadow-none">
    <CardContent className="py-4">
      <div className="flex gap-3">
        <Info className="h-5 w-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
        <div className="space-y-2 text-sm text-[#475569]">
          <p className="font-medium text-[#0F172A]">Hypothèses de calcul et contraintes</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>
              <strong>Pression amont:</strong> Pression disponible à l'entrée du système de récupération
            </li>
            <li>
              <strong>Pression aval minimale ({PRESSION_AVAL_MIN} bars):</strong> Seuil de service incompressible pour garantir le fonctionnement du réseau AEP
            </li>
            <li>
              <strong>Pression exploitable:</strong> Différence entre pression amont et seuil minimal = max(P.amont - {PRESSION_AVAL_MIN}, 0)
            </li>
            <li>
              <strong>Puissance théorique:</strong> P = 27,78 × Q × ΔP (m³/h) ou P = 1,667 × Q × ΔP (L/min)
            </li>
            <li>
              <strong>Puissance utile:</strong> Puissance réellement exploitable après application du rendement de la turbine
            </li>
            <li>
              <strong>Tarifs:</strong> Vente EDF = 0,04 €/kWh | Autoconsommation = 0,19 €/kWh
            </li>
          </ul>
          <p className="text-xs italic pt-2 border-t border-[#CBD5E1]">
            <Droplets className="h-3 w-3 inline mr-1" />
            Note: La récupération réelle dépend des contraintes hydrauliques du réseau. Les {PRESSION_AVAL_MIN} bars de pression aval 
            constituent une réserve de service non exploitable pour garantir l'alimentation en eau des usagers.
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

/**
 * Composant de pied de page
 */
const Footer = () => (
  <footer className="text-center text-xs text-[#475569] py-4 space-y-2">
    <p>
      Calculatrice de potentiel énergétique AEP — Outil d'estimation théorique
    </p>
    <p className="font-medium text-[#0F172A]">
      ELECTRON INDUSTRIE - Bureau d'études Industrie Mécanique
    </p>
  </footer>
);

/**
 * Composant principal de la calculatrice
 */
export const EnergyCalculator = () => {
  const {
    pressionAmont,
    setPressionAmont,
    unite,
    debit,
    setDebit,
    rendement,
    setRendement,
    mode,
    setMode,
    showTable,
    setShowTable,
    searchTerm,
    setSearchTerm,
    sortConfig,
    copied,
    pressionExploitable,
    debitsDisponibles,
    dureeHeures,
    resultats,
    tableauTrie,
    handleUniteChange,
    handleSort,
    handleReset,
    handleCopy,
    handleExportCSV
  } = useAEPCalculator();

  return (
    <TooltipProvider>
      <div 
        className="min-h-screen bg-[#F8FAFC] p-4 md:p-6 lg:p-8"
        data-testid="energy-calculator-container"
      >
        <div className="max-w-7xl mx-auto space-y-6">
          
          <Header />

          {/* Grille principale: Paramètres + Résultats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ParametersCard
              pressionAmont={pressionAmont}
              setPressionAmont={setPressionAmont}
              pressionExploitable={pressionExploitable}
              unite={unite}
              handleUniteChange={handleUniteChange}
              debit={debit}
              setDebit={setDebit}
              debitsDisponibles={debitsDisponibles}
              rendement={rendement}
              setRendement={setRendement}
              mode={mode}
              setMode={setMode}
              onReset={handleReset}
              onCopy={handleCopy}
              copied={copied}
            />

            <ResultsCard
              resultats={resultats}
              mode={mode}
              dureeHeures={dureeHeures}
              pressionAmont={pressionAmont}
              pressionExploitable={pressionExploitable}
              debit={debit}
              unite={unite}
              rendement={rendement}
            />
          </div>

          <TechnicalNotes />

          <CombinationsTable
            showTable={showTable}
            setShowTable={setShowTable}
            tableauTrie={tableauTrie}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            sortConfig={sortConfig}
            handleSort={handleSort}
            onExportCSV={handleExportCSV}
          />

          <Footer />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default EnergyCalculator;
